import { type PropsWithChildren, useMemo } from 'react';
import styled, { css } from 'styled-components';
import {
  i18nInit,
  useLanguageControlOverrides,
  useLoadLocale
} from '@elfsight/lib-i18n';
import { LOCAL_WIDGET_ID, WidgetTokenProvider } from '@elfsight/lib-utils';
import { API_FORM_SERVICE_NAME } from '@elfsight/widget-form-builder/widget';
import { useCustomJS, useDefineAPISchema } from '@elfsight/widget-js-api';
import { PortalNestProvider } from '@elfsight/widget-modal';
import {
  CustomCSS,
  createCustomCSSPrefixClassName,
  EmbedRoot as LibEmbedRoot,
  WidgetFontsLoader
} from '@elfsight/widget-ui';
import { I18nProvider } from '@lingui/react';
import { useSettings } from '@wu/widget-core';
import CATALOG from '../i18n/languages/widget/catalog.json';
import { meta } from './constants';
import { type PricingTableSettings } from './types';
import { useTablesHaveProperty } from './utils';
import { FormPopupProvider } from './widget/form-popup';
import { TablesContextProvider } from './widget/tables-provider';
import { Widget } from './widget/widget';

const { alias } = meta;

export function PricingTable(_settings: PricingTableSettings) {
  return (
    <PricingTableProviders>
      <Widget />
    </PricingTableProviders>
  );
}

const PricingTableProviders = ({ children }: PropsWithChildren) => {
  const {
    customCSS,
    customJS,
    font,
    language,
    width,
    editMode,
    renderThumbnailMode,
    widgetId,
    widgetToken,
    tables
  } = useSettings();

  const i18n = useMemo(() => i18nInit(), []);

  const customCSSPrefixClassName = createCustomCSSPrefixClassName(
    alias,
    widgetId
  );
  const { language: locale } = language;

  const { form: hasForm } = useTablesHaveProperty(tables);
  useDefineAPISchema({
    [API_FORM_SERVICE_NAME]: hasForm
  });
  useCustomJS(customJS, editMode || renderThumbnailMode);

  const messages = useLoadLocale(locale, 'widget', i18n);
  useLanguageControlOverrides({
    language,
    messages,
    catalog: CATALOG,
    i18n
  });

  return (
    <I18nProvider i18n={i18n}>
      <WidgetTokenProvider
        widgetToken={widgetToken}
        widgetId={widgetId ?? LOCAL_WIDGET_ID}
      >
        <PortalNestProvider customCSSClassName={customCSSPrefixClassName}>
          <TablesContextProvider>
            <FormPopupProvider>
              <CustomCSS
                customCSS={customCSS}
                prefixClassName={customCSSPrefixClassName}
              />
              <WidgetFontsLoader fonts={[font]} />

              <EmbedRoot
                customCSSClassName={customCSSPrefixClassName}
                font={font}
                width={width}
                $renderThumbnailMode={renderThumbnailMode}
              >
                {children}
              </EmbedRoot>
            </FormPopupProvider>
          </TablesContextProvider>
        </PortalNestProvider>
      </WidgetTokenProvider>
    </I18nProvider>
  );
};

const EmbedRoot = styled(LibEmbedRoot)<{
  $renderThumbnailMode?: boolean;
}>`
  ${({ $renderThumbnailMode }) =>
    $renderThumbnailMode &&
    css`
      padding: 30px;
      border-radius: 20px;
      background-color: #fff;
      box-shadow: 0 6px 30px rgba(0, 0, 0, 0.1);
      z-index: 1;
    `}
`;
